import { fetchOrgData, OrgUrls } from '@api/org';
import { useAlert } from '@hooks/useAlert';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@features/redux/store';
import type { FilterInput } from './EmployeeRank';
import { Button } from '@components/design-system';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

interface EmployeeExportProps {
    filters: FilterInput;
    rankBy: 'totalCuts' | 'warranty' | 'successful' | 'warrantyPercentage';
    searchText: string;
    dateRange: { startDate: string; endDate: string };
    material: number | string | 'All';
}

export const EmployeeExport = ({ filters, rankBy, searchText, dateRange, material }: EmployeeExportProps) => {
    const [csvDownloading, setCsvDownloading] = useState(false);

    const { IdToken } = useSelector((state: RootState) => state.auth);

    const { setAlert } = useAlert();

    const getEmployeesExport = async () => {
        const csv = await fetchOrgData<string>(IdToken!, OrgUrls.LocationEmployeesCSV, {
            companyIds: Object.keys(filters.companies)
                .filter((key) => filters.companies[key])
                .join(','),
            resellerIds: Object.keys(filters.resellers)
                .filter((key) => filters.resellers[key])
                .join(','),
            locationIds: Object.keys(filters.locations)
                .filter((key) => filters.locations[key])
                .join(','),
            rankBy,
            searchText,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            material: material === 'All' ? undefined : material,
        });

        return csv;
    };

    const downloadCSV = async () => {
        setCsvDownloading(true);
        const data = await getEmployeesExport().catch((err) => setAlert(err.message, 'error'));
        setCsvDownloading(false);

        const csvData = `data:text/csv;base64,${btoa(unescape(encodeURIComponent(data!)))}`;

        const link = document.createElement('a');
        link.setAttribute('href', csvData);
        link.setAttribute('download', `EmployeeRank-${new Date().toISOString()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Button
            variant='secondary'
            size='small'
            onClick={downloadCSV}
            loading={csvDownloading}
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}>
            Export
        </Button>
    );
};
